import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input } from '@angular/core';
import { type IncidentDto } from '@api/incidents';
import { IncidentSeverity } from '@models';
import { TippyDirective } from '@ngneat/helipopper';

import { IncidentSeverityIconComponent } from '../incident-status/incident-severity-icon.component';

export type IncidentSeverityCountVariant = 'icon' | 'pill';

const toCount = (value: number | null | undefined) => value ?? 0;

const severityClass = (severity: IncidentSeverity) => severity === IncidentSeverity.CRITICAL ? 'severity-critical' : 'severity-warning';

const PILL_CLASS = 'severity-block test-severity-pill';

const ICON_CLASS = 'severity-icons-item inline-flex items-center gap-0.5';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [IncidentSeverityIconComponent, TippyDirective],
  selector: 'avs-incident-severity-count',
  styleUrl: './incident-severity-count.component.scss',
  templateUrl: './incident-severity-count.component.html',
})
export class IncidentSeverityCountComponent {
  /* Pass the counts per severity or raw incidents to count; incidents win when both are set */
  critical = input(0, { transform: toCount });
  major = input(0, { transform: toCount });
  minor = input(0, { transform: toCount });
  incidents = input<Pick<IncidentDto, 'severity'>[] | null>();
  variant = input<IncidentSeverityCountVariant>('pill');
  /* One total with a breakdown popper instead of a value per severity */
  compact = input(false, { transform: booleanAttribute });
  /* Not compact: keep critical and minor visible at 0, major only when present. Compact: show a plain 0 */
  showZero = input(false, { transform: booleanAttribute });

  /* Touch devices fire mouseenter on tap, which would pop the tooltip open instead of acting on the pill */
  protected readonly canHover = typeof matchMedia === 'function' ? matchMedia('(hover: hover)').matches : true;

  protected isIcon = computed(() => this.variant() === 'icon');

  protected values = computed(() => {
    const incidents = this.incidents();

    if (incidents) {
      const counts: Record<IncidentSeverity, number> = {
        [IncidentSeverity.CRITICAL]: 0,
        [IncidentSeverity.MAJOR]: 0,
        [IncidentSeverity.MINOR]: 0,
      };

      incidents.forEach(({ severity }) => counts[severity]++);

      return counts;
    }

    return {
      critical: this.critical(),
      major: this.major(),
      minor: this.minor(),
    };
  });

  protected total = computed(() => {
    const { critical, major, minor } = this.values();

    return critical + major + minor;
  });

  protected highestSeverity = computed(() => {
    const { critical, major } = this.values();

    if (critical) {
      return IncidentSeverity.CRITICAL;
    }

    return major ? IncidentSeverity.MAJOR : IncidentSeverity.MINOR;
  });

  protected breakdownItems = computed(() => {
    const { critical, major, minor } = this.values();

    return [
      {
        count: critical,
        severity: IncidentSeverity.CRITICAL,
      },
      {
        count: major,
        severity: IncidentSeverity.MAJOR,
      },
      {
        count: minor,
        severity: IncidentSeverity.MINOR,
      },
    ];
  });

  protected items = computed(() => {
    const showZero = this.showZero();

    return this.breakdownItems()
      .filter(({ count, severity }) => count > 0 || (showZero && severity !== IncidentSeverity.MAJOR))
      .map(({ count, severity }) => ({
        count,
        cssClass: this.isIcon() ? ICON_CLASS : `${PILL_CLASS} ${severityClass(severity)}`,
        label: `${count} ${severity}`,
        severity,
      }));
  });

  protected compactClass = computed(() => this.isIcon() ? ICON_CLASS : `${PILL_CLASS} ${severityClass(this.highestSeverity())}`);

  protected compactLabel = computed(() => {
    const [critical, major, minor] = this.breakdownItems();

    return `${this.total()} total: ${critical.count} critical, ${major.count} major, ${minor.count} minor`;
  });
}
